import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatINR } from "@/lib/api";
import { LOAN_TYPES, PREFILL_KEY, calcEmi, slug } from "@/lib/constants";
import { useRates } from "@/context/RatesContext";
import { Navbar } from "@/components/Navbar";
import { FileUploader, DocumentList } from "@/components/FileUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Home, Building2, Briefcase, ArrowLeft, ArrowRight, Loader2, ShieldCheck } from "lucide-react";

const ICONS = { "Home Loan": Home, LAP: Building2, "Business Loan": Briefcase };
const STEPS = ["Loan", "Income", "Property", "Documents"];

export default function NewApplication() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const { rates: RATES } = useRates();
  const [form, setForm] = useState({ loan_type: "Home Loan", requested_amount: "", tenure_years: "20", monthly_income: "", property_address: "", interest_rate: String(RATES["Home Loan"]) });
  const rateTouched = useRef(false);
  useEffect(() => {
    if (!rateTouched.current) setForm((f) => ({ ...f, interest_rate: String(RATES[f.loan_type]) }));
  }, [RATES]);
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current) return;
    prefilled.current = true;
    try {
      const p = JSON.parse(sessionStorage.getItem(PREFILL_KEY) || "null");
      if (!p) return;
      sessionStorage.removeItem(PREFILL_KEY);
      rateTouched.current = true;
      setForm((f) => ({ ...f, loan_type: p.loan_type, requested_amount: String(p.requested_amount), tenure_years: String(p.tenure_years), monthly_income: String(p.monthly_income), interest_rate: String(p.interest_rate ?? RATES[p.loan_type]) }));
      toast.success("Pre-filled from your eligibility check");
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [app, setApp] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { if (k === "interest_rate") rateTouched.current = true; setForm({ ...form, [k]: e.target.value }); };
  const isBusiness = form.loan_type === "Business Loan";

  const canNext = [
    Number(form.requested_amount) > 0,
    Number(form.monthly_income) > 0,
    isBusiness || form.property_address.trim().length > 5,
    true,
  ][step];

  const createApplication = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/applications", {
        loan_type: form.loan_type,
        requested_amount: Number(form.requested_amount),
        monthly_income: Number(form.monthly_income),
        tenure_years: Number(form.tenure_years) || null,
        interest_rate: Number(form.interest_rate) || null,
        property_address: isBusiness ? null : form.property_address.trim(),
      });
      setApp(data);
      setStep(3);
      toast.success("Application created — now upload your documents");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const next = () => (step === 2 ? createApplication() : setStep(step + 1));

  return (
    <div className="min-h-screen bg-[#0B132B] text-white">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-10 lg:py-16" data-testid="new-application-page">
        <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#FF6B00]">New application</p>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold">Tell us about your requirement.</h1>

        <ol className="mt-8 grid grid-cols-4 gap-2" data-testid="wizard-steps">
          {STEPS.map((s, i) => (
            <li key={s} className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-[#FF6B00]" : "bg-white/15"}`} title={s} />
          ))}
        </ol>
        <p className="mt-2 text-sm text-slate-400" data-testid="wizard-step-label">Step {step + 1} of 4 · {STEPS[step]}</p>

        <section className="mt-6 bg-white text-[#0F172A] rounded-2xl p-6 sm:p-8 shadow-2xl">
          {step === 0 && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-3 gap-3">
                {LOAN_TYPES.map((t) => {
                  const Icon = ICONS[t];
                  const active = form.loan_type === t;
                  return (
                    <button type="button" key={t} onClick={() => setForm({ ...form, loan_type: t, interest_rate: String(RATES[t]) })} data-testid={`loan-type-${slug(t)}`}
                      className={`rounded-xl border-2 p-4 text-left transition-colors ${active ? "border-[#FF6B00] bg-[#FFF3E8]" : "border-slate-200 hover:border-slate-300"}`}>
                      <Icon className={`h-6 w-6 ${active ? "text-[#E05E00]" : "text-slate-400"}`} />
                      <p className="mt-3 font-semibold text-sm">{t}</p>
                    </button>
                  );
                })}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="amount">Requested amount (₹)</Label>
                  <Input id="amount" type="number" min="100000" step="100000" value={form.requested_amount} onChange={set("requested_amount")} placeholder="1,25,00,000" className="mt-1.5 h-11" data-testid="requested-amount-input" />
                  {form.requested_amount > 0 && <p className="mt-1 text-xs text-slate-500">{formatINR(form.requested_amount)}</p>}
                </div>
                <div>
                  <Label htmlFor="tenure">Tenure (years)</Label>
                  <Input id="tenure" type="number" min="1" max="30" value={form.tenure_years} onChange={set("tenure_years")} className="mt-1.5 h-11" data-testid="tenure-input" />
                </div>
                <div>
                  <Label htmlFor="rate">Interest rate (% p.a.)</Label>
                  <Input id="rate" type="number" min="5" max="24" step="0.05" value={form.interest_rate} onChange={set("interest_rate")} className="mt-1.5 h-11" data-testid="interest-rate-input" />
                  <p className="mt-1 text-xs text-slate-500">Indicative for {form.loan_type}: {RATES[form.loan_type].toFixed(2)}%</p>
                </div>
                {Number(form.requested_amount) > 0 && Number(form.interest_rate) > 0 && (
                  <div className="rounded-lg bg-[#FFF3E8] border border-orange-200 px-4 py-3 text-sm" data-testid="wizard-emi-preview">
                    Indicative EMI <strong>{formatINR(calcEmi(Number(form.requested_amount), Number(form.interest_rate), Number(form.tenure_years) || 20))}</strong> / month
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="income">Net monthly income (₹)</Label>
                <Input id="income" type="number" min="10000" step="5000" value={form.monthly_income} onChange={set("monthly_income")} placeholder="3,50,000" className="mt-1.5 h-11" data-testid="monthly-income-input" />
                {form.monthly_income > 0 && <p className="mt-1 text-xs text-slate-500">{formatINR(form.monthly_income)} per month</p>}
              </div>
              <p className="text-sm text-slate-500 flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[#FF6B00] mt-0.5 shrink-0" /> Income details are used solely for eligibility assessment and are never shared without consent.</p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {isBusiness ? (
                <p className="text-sm text-slate-600" data-testid="property-skip-note">Business Loans do not require a property. You may add a collateral address optionally.</p>
              ) : null}
              <div>
                <Label htmlFor="address">Property address {isBusiness && "(optional)"}</Label>
                <Textarea id="address" rows={4} value={form.property_address} onChange={set("property_address")} placeholder="Flat / Tower, Project, Street, City, PIN" className="mt-1.5" data-testid="property-address-input" />
              </div>
            </div>
          )}

          {step === 3 && app && (
            <div className="space-y-6">
              <div className="rounded-xl bg-[#FFF3E8] border border-orange-200 p-4 text-sm">
                <p className="font-semibold">Application <span className="text-[#E05E00]">{app.id.replace("app_", "").toUpperCase()}</span> created for {formatINR(app.requested_amount)}.</p>
                <p className="text-slate-600 mt-1">Upload your PAN, Aadhar, Bank Statements and Property Chain documents to move your file forward.</p>
              </div>
              <FileUploader applicationId={app.id} onUploaded={(d) => setDocs([...docs, d])} />
              {docs.length > 0 && <DocumentList documents={docs} />}
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            {step > 0 && step < 3 ? (
              <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="wizard-back-button"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
            ) : <span />}
            {step < 3 ? (
              <Button className="gold-btn rounded-full px-6 h-11" disabled={!canNext || busy} onClick={next} data-testid="wizard-next-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : step === 2 ? "Create application" : "Continue"} {!busy && <ArrowRight className="h-4 w-4 ml-2" />}
              </Button>
            ) : (
              <Button className="gold-btn rounded-full px-6 h-11" onClick={() => navigate("/dashboard")} data-testid="wizard-finish-button">
                {docs.length ? "Go to my dashboard" : "Skip for now"} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
